import Webcam from 'react-webcam'
import UIFx from 'uifx'
import _ from 'lodash'

import { GameState, GameStatus, GameContainerDimensions } from '../types/game'
import {
  getBoundingBoxCoords,
  generateBoxCoords,
  computeIou,
  computeArea,
  flipResults,
} from '../utils/box.js'

import * as handpose from '@tensorflow-models/handpose'

import { drawBoxCoords, drawHands, drawTargets } from '../utils/model'
import CoinImage from '../assets/Coins-Square.svg'
import OrangeImage from '../assets/Oranges-Square.svg'
import GoldImage from '../assets/gold.svg'
import FanImage from '../assets/Fan.svg'

// Sound effect retrieved from http://freesoundeffect.net
import CollectSfx from '../assets/collect.mp3'

const IMG_SRC = {
  coin: CoinImage,
  orange: OrangeImage,
  gold: GoldImage,
  fan: FanImage,
}
const SFX_SRC = CollectSfx

interface Target {
  coordinates: number[]
  type: string
}

export class BalloonGame {
  model: any
  targets: Target[]
  dimensions: GameContainerDimensions
  imgRef: { [type: string]: HTMLImageElement } | null
  sfxRef: UIFx
  loaded: boolean
  settings: Record<string, any>
  remainingDelay: number[]

  constructor(
    dimensions: GameContainerDimensions,
    settings: Record<string, any>
  ) {
    this.targets = []
    this.dimensions = dimensions
    this.settings = settings
    this.model = null
    this.imgRef = {}
    this.loaded = false
    this.sfxRef = new UIFx(SFX_SRC, {
      volume: 0.4, // number between 0.0 ~ 1.0
      throttleMs: 100,
    })
    this.remainingDelay = []
    for (let i = 0; i < this.settings.numBalloons; i++) {
      this.remainingDelay.push(0)
    }
  }

  static async create(
    dimensions: GameContainerDimensions,
    settings: Record<string, any> = {}
  ): Promise<BalloonGame> {
    const game = new BalloonGame(dimensions, settings)
    await game.loadGame()
    return game
  }

  async loadGame(): Promise<void> {
    this.model = await this.loadModel()
    this.imgRef = Object.fromEntries(
      await Promise.all(
        Object.entries(IMG_SRC).map(async ([k, v]) => [
          k,
          await this.loadImg(v),
        ])
      )
    )
    this.loaded = true
  }

  async loadModel(): Promise<void> {
    return await handpose.load()
  }

  async loadImg(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve) => {
      const imgRef = new Image()
      imgRef.src = src
      imgRef.onload = () => {
        resolve(imgRef)
      }
    })
  }

  async renderFrame(
    ctx: CanvasRenderingContext2D | null,
    targets: Target[],
    imgRef: { [type: string]: HTMLImageElement } | null
  ): Promise<void> {
    ctx!.clearRect(0, 0, this.dimensions!.width, this.dimensions!.height)
    drawTargets(imgRef, targets, ctx)
  }

  async generateBalloons(): Promise<void> {
    if (this.settings.movingTargets) {
      const displacedTargets = this.targets.map(
        (target): Target => {
          return {
            ...target,
            coordinates: [
              target.coordinates[0],
              target.coordinates[1] +
                this.settings.relativeDrop * this.dimensions.height,
              target.coordinates[2],
              target.coordinates[3] +
                this.settings.relativeDrop * this.dimensions.height,
            ],
          }
        }
      )
      // remove targets that drop below screen
      this.targets = displacedTargets.filter((target) => {
        return target.coordinates[3] < this.dimensions.height
      })
      for (
        let i = 0;
        i <
        this.settings.numBalloons -
          this.remainingDelay.length -
          this.targets.length;
        i++
      ) {
        this.remainingDelay.unshift(0)
      }
    }

    const newRemainingDelay = this.remainingDelay.filter(
      (remaining) => remaining !== 0
    )
    for (
      let i = 0;
      i < this.remainingDelay.length - newRemainingDelay.length;
      i++
    ) {
      const widthMinMax = [200, 220]
      this.targets.push({
        coordinates: generateBoxCoords(
          widthMinMax,
          widthMinMax,
          [0, this.dimensions!.width],
          [0, this.dimensions!.height]
        ),
        type: Object.keys(this.imgRef!)[
          _.random(0, Object.keys(this.imgRef!).length - 1)
        ],
      })
    }
    this.remainingDelay = newRemainingDelay
  }

  getBoundingBoxes(results: any): number[][] {
    return results.map((result: any) => getBoundingBoxCoords(result.landmarks))
  }

  getRelativeArea(preds: number[][]): number[] {
    if (this.dimensions) {
      const screenArea = this.dimensions.width * this.dimensions.height
      const relativeAreas = preds.map((pred) => computeArea(pred) / screenArea)
      return relativeAreas
    }
    return []
  }

  calculateScore(
    preds: number[][],
    targets: Target[],
    minIou = this.settings.minIou
  ): number {
    const remainingTargets = targets.filter((target) => {
      return !preds.some((pred) => {
        const Iou = computeIou(pred, target.coordinates)
        return Iou > minIou
      })
    })
    const numPopped =
      this.settings.numBalloons -
      this.remainingDelay.length -
      remainingTargets.length
    for (let i = 0; i < numPopped; i++) {
      this.remainingDelay.push(this.settings.targetDelay)
    }

    const score = numPopped * this.settings.pointsPerBalloon
    this.targets = remainingTargets
    return score
  }

  /**
   * Paint new canvas and return next game state
   * @param webcam
   * @param overlay
   * @param gameState
   */
  async update(
    webcam: Webcam,
    overlay: HTMLCanvasElement,
    gameState: GameState
  ): Promise<GameState> {
    // If game finished reset game state and clear screen
    if (gameState.status === GameStatus.Finished) {
      this.targets = []
      this.renderFrame(overlay.getContext('2d')!, this.targets, this.imgRef)
      return { ...gameState, prevStatus: gameState.status }
    }

    if (gameState.status === GameStatus.Loading) {
      if (this.loaded && webcam.video && webcam.video.readyState === 4) {
        return {
          instructions: 'Game is ready',
          status: GameStatus.Ready,
          prevStatus: gameState.status,
          score: 0,
        }
      }
    }

    if (gameState.status === GameStatus.Start) {
      // check for play button tapped
      return {
        ...gameState,
        instructions: 'Raise up one hand!',
        status: GameStatus.Calibrating,
        prevStatus: gameState.status,
      }
    }

    if (gameState.status === GameStatus.Calibrating) {
      const video = webcam.video
      const results = await this.model.estimateHands(video, true)
      const preds = this.getBoundingBoxes(results)
      const relativeHandAreas = this.getRelativeArea(preds)

      let newInstructions
      let newStatus
      if (preds.length > 0) {
        if (relativeHandAreas[0] < this.settings.minRelativeHandArea) {
          newInstructions = 'Move closer to screen'
        } else if (relativeHandAreas[0] > this.settings.maxRelativeHandArea) {
          newInstructions = 'Move further from screen'
        } else {
          newInstructions = 'Grab the goodies!'
          newStatus = GameStatus.Playing
        }
      }
      return {
        ...gameState,
        instructions: newInstructions || gameState.instructions,
        status: newStatus || gameState.status,
        prevStatus: gameState.status,
      }
    }

    if (gameState.status === GameStatus.Playing) {
      this.generateBalloons()
      this.renderFrame(overlay.getContext('2d')!, this.targets, this.imgRef)
      const reversedResults = await this.model.estimateHands(webcam.video, true)
      const results = flipResults(reversedResults, this.dimensions)
      const preds = this.getBoundingBoxes(results)
      const relativeHandAreas = this.getRelativeArea(preds)

      if (preds.length > 0) {
        let calibrateInstructions
        if (relativeHandAreas[0] < this.settings.minRelativeHandArea) {
          calibrateInstructions = 'Move closer to screen'
        }
        if (relativeHandAreas[0] > this.settings.maxRelativeHandArea) {
          calibrateInstructions = 'Move further from screen'
        }
        if (calibrateInstructions) {
          return {
            ...gameState,
            prevStatus: gameState.status,
            instructions: calibrateInstructions,
          }
        }
      }

      // draw bounding box for debugging purposes
      if (process.env.NODE_ENV === 'development' && preds.length > 0) {
        drawHands(results, overlay.getContext('2d')!)
        drawBoxCoords(preds, overlay.getContext('2d'))
      }
      // Decrease remaining timer before items reappear
      for (let i = 0; i < this.remainingDelay.length; i++) {
        this.remainingDelay[i] -= 1
      }
      // calculate popped targets
      const scoreDiff = this.calculateScore(preds, this.targets)
      if (scoreDiff) {
        this.sfxRef.play()
      }
      return {
        ...gameState,
        prevStatus: gameState.status,
        score: gameState.score + scoreDiff,
        instructions: scoreDiff ? 'Nice job!' : 'Grab the goodies!',
      }
    }
    return {
      ...gameState,
      prevStatus: gameState.status,
    }
  }
}
